import { ref } from 'vue';
import Pusher from 'pusher-js/with-encryption';
import useAdobeService from '@/services/adobe';
import { PUSHER_KEY, PUSHER_CLUSTER } from '@/config/appConfig';
import shouldRetryFetch from '@/utils/shouldRetryFetch';

/* ***** User Channels ***** */

const pusherInstance = ref(null); // Generic connection

function initPusher() {
  pusherInstance.value = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
  return pusherInstance.value;
}

function subscribeToUserChannel(userId) {
  if (!userId) return undefined;
  if (!pusherInstance.value) initPusher();
  return pusherInstance.value.subscribe(`user-${userId}`);
}

function unsubscribeFromUserChannel(userId) {
  if (!userId) return undefined;
  if (!pusherInstance.value) initPusher();
  return pusherInstance.value.unsubscribe(`user-${userId}`);
}

/* ***** Spark Channels ***** */

const pusherInstanceSpark = ref(null); // Generic connection

function initPusherSpark() {
  pusherInstanceSpark.value = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
  return pusherInstanceSpark.value;
}

function subscribeToSparkChannel(userId, businessUnitId) {
  if (!userId || !businessUnitId) return undefined;
  if (!pusherInstanceSpark.value) initPusherSpark();
  return pusherInstanceSpark.value.subscribe(`user_${userId}_businessunit_${businessUnitId}`);
}

function unsubscribeFromSparkChannel(userId, businessUnitId) {
  if (!userId || !businessUnitId) return undefined;
  if (!pusherInstanceSpark.value) initPusherSpark();
  return pusherInstanceSpark.value.unsubscribe(`user_${userId}_businessunit_${businessUnitId}`);
}

/* ***** Adobe Channels ***** */

const pusherInstanceAdobe = ref(null); // Authed connection for encrypted Adobe notifications.

const makePusherAuth = async (userId, pusherPayload, retryCount) => useAdobeService()
  .makePusherAuth(userId, pusherPayload, {
    async onFetchError(error) {
    // retry if the error is a network error or a 5xx server error
      if (shouldRetryFetch(error)) {
        if (retryCount > 0) {
          setTimeout(async () => {
            await makePusherAuth(userId, pusherPayload, retryCount - 1);
          }, 1000); // Throttle by 1 second

          return error;
        }
      }

      // because we are outside of the component when this function is called,
      // we don't have access to Toast component
      const { default: useToastsStore } = await import('@/stores/toasts');
      const { addToToastQueue } = useToastsStore();

      addToToastQueue({
        severity: 'error',
        detail: 'There is an issue with the Adobe PSD upload service. The feature may not work as expected. Please refresh or try again later.', // eslint-disable-line max-len, vue/max-len
        life: 5000,
      });

      return error;
    },
  });

const adobeCustomHandler = (userId) => async ({ channelName, socketId }, cb) => {
  const pusherPayload = {
    userId,
    channelName,
    socketId,
  };

  const {
    data: pusherAuthData,
    error: pusherAuthError,
  } = await makePusherAuth(userId, pusherPayload, 3);

  cb(pusherAuthError?.value, pusherAuthData?.value);
};

function initAdobePusher(userId) {
  const customHandler = adobeCustomHandler(userId);

  pusherInstanceAdobe.value = new Pusher(PUSHER_KEY, {
    cluster: PUSHER_CLUSTER,
    enabledTransports: ['wss', 'ws', 'xhr_streaming'],
    channelAuthorization: { customHandler },
    userAuthentication: { customHandler },
  });

  return pusherInstanceAdobe.value.signin();
}

function subscribeToAdobeNotifications(userId) {
  if (!userId) return undefined;
  return pusherInstanceAdobe.value.subscribe(`private-encrypted-notifications_${userId}`);
}

function unsubscribeFromAdobeNotifications(userId) {
  if (!userId) return undefined;
  return pusherInstanceAdobe.value.unsubscribe(`private-encrypted-notifications_${userId}`);
}

/* ***** Exports ***** */

export default () => {
  const init = async (userId) => {
    const user = initPusher();
    const adobe = await initAdobePusher(userId);
    const spark = initPusherSpark();

    return {
      adobe,
      user,
      spark,
    };
  };

  return {
    instances: {
      adobe: pusherInstanceAdobe?.value || pusherInstanceAdobe,
      user: pusherInstance?.value || pusherInstance,
      spark: pusherInstanceSpark?.value || pusherInstanceSpark,
    },

    init,

    subscribeToAdobeNotifications,
    unsubscribeFromAdobeNotifications,

    subscribeToUserChannel,
    unsubscribeFromUserChannel,

    subscribeToSparkChannel,
    unsubscribeFromSparkChannel,
  };
};
